import type { DeliverableStatus, Metric, Unit } from '@pixerito/api-client';

const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const MONTHS_SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** "2026-08" → "agosto de 2026" */
export function monthLong(period: string | null | undefined) {
  if (!period) return '';
  const [y, m] = period.split('-').map(Number);
  return `${MONTHS[m - 1]} de ${y}`;
}

/** "2026-08" → "ago/26" */
export function monthShort(period: string) {
  const [y, m] = period.split('-').map(Number);
  return `${MONTHS_SHORT[m - 1]}/${String(y).slice(2)}`;
}

/** "2026-08" → "Agosto" */
export function monthName(period: string | null | undefined) {
  if (!period) return '';
  const m = Number(period.split('-')[1]);
  const name = MONTHS[m - 1];
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function shiftPeriod(period: string, months: number) {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + months, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const intFmt = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const oneFmt = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const compactFmt = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 });

export function formatValue(value: number | null | undefined, unit: Unit) {
  if (value === null || value === undefined) return '—';
  if (unit === 'percent') return `${oneFmt.format(value)}%`;
  if (unit === 'rating') return oneFmt.format(value);
  return intFmt.format(value);
}

export function formatInt(value: number) {
  return intFmt.format(value);
}

export function formatCompact(value: number) {
  return value >= 10000 ? compactFmt.format(value) : intFmt.format(value);
}

/** Variação já calculada pela API: % para contagens, pontos para percentuais e notas. */
export function formatDelta(metric: Pick<Metric, 'delta' | 'deltaKind' | 'unit'>) {
  if (metric.delta === null) return null;
  const abs = Math.abs(metric.delta);
  const sign = metric.delta > 0 ? '+' : metric.delta < 0 ? '−' : '';
  if (metric.deltaKind === 'percent') return `${sign}${oneFmt.format(abs)}%`;
  return `${sign}${oneFmt.format(abs)} ${metric.unit === 'rating' ? 'pt' : 'p.p.'}`;
}

export function percentChange(current: number, previous: number | null | undefined) {
  if (previous === null || previous === undefined || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

export function formatDate(iso: string | null | undefined) {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }).replace('.', '');
}

/** "26 set" (com o ano só se não for o atual). */
export function formatDay(iso: string | null | undefined) {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}${sameYear ? '' : ` ${d.getFullYear()}`}`;
}

export function formatDateTime(iso: string | null | undefined) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '')}, ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

export function relativeDays(iso: string | null | undefined) {
  if (!iso) return 'nunca';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  if (days < 30) return `há ${days} dias`;
  return formatDate(iso);
}

export const STATUS_LABEL: Record<DeliverableStatus, string> = {
  in_production: 'Em produção',
  in_review: 'Em revisão',
  delivered: 'Entregue',
};

export const STATUS_ORDER: DeliverableStatus[] = ['in_production', 'in_review', 'delivered'];

export const MODULE_LABEL: Record<string, string> = {
  social: 'Redes Sociais',
  site: 'Site',
  gmb: 'Google Meu Negócio',
  brand: 'Identidade Visual',
  video: 'Vídeo',
};

export function hostOf(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
